import { ApolloClient, InMemoryCache, NormalizedCacheObject, defaultDataIdFromObject, Reference } from '@apollo/client';
import React, { ReactNode } from 'react';
import { createGraphQLApiProvider } from '@stitch-fix/sf-next';
export const setupCache = () => new InMemoryCache({
  dataIdFromObject: object => {
    if (object.__typename === 'ShopProductVariant' && typeof (object as {
      sku?: string;
    }).sku === 'string' && (object as {
      sku: string;
    }).sku) {
      return `ShopProductVariant:${(object as {
        sku: string;
      }).sku}`;
    }
    return defaultDataIdFromObject(object);
  },
  typePolicies: {
    FreestyleSearch: {
      fields: {
        /*
         * Cache results based on q and page variables passed to the getSearchResults query.
         * No need to include the filters and sort since the Graph returns a new `FreestyleSearch.id`
         * value thus creating a separate entry in the cache.
         *
         * source: https://www.apollographql.com/docs/react/pagination/key-args/#setting-keyargs
         */
        shopProductsByPage: {
          keyArgs: ['$q', '$page']
        }
      }
    },
    FreestyleSearchFilter: {
      fields: {
        /*
         * Cache the filter options based on q and filters variables.
         * Since the filter options are "dynamic", the Graph returns different
         * filter options based on the filters applied to a query, e.g.,
         * - Initial query has four occasion filter options
         * - Client then selects a color option
         * - Follow-up query might then return only two occasion filter options
         *
         * source: https://www.apollographql.com/docs/react/caching/cache-field-behavior/#specifying-key-arguments
         */
        options: {
          keyArgs: ['$q', '$filters']
        }
      }
    },
    Vision: {
      fields: {
        previousLooks: {
          keyArgs: false,
          merge(existing, incoming) {
            if (!incoming) {
              return existing;
            }
            const existingNodes: Reference[] = existing?.nodes ?? [];
            const incomingNodes: Reference[] = incoming?.nodes ?? [];

            /* eslint-disable no-underscore-dangle */
            const seen = new Set(existingNodes.map(n => n.__ref));
            const dedupedIncoming = incomingNodes.filter(n => !seen.has(n.__ref));
            /* eslint-enable no-underscore-dangle */

            return {
              __typename: 'LookConnection',
              nodes: [...existingNodes, ...dedupedIncoming],
              pageInfo: incoming?.pageInfo ?? existing?.pageInfo ?? null
            };
          }
        }
      }
    },
    Client: {
      fields: {
        currentFixPreview: {
          merge: false
        },
        fixContent: {
          merge: true
        }
      }
    },
    Query: {
      fields: {
        client: {
          merge: true
        },
        shopContent: {
          merge: true
        }
      }
    }
  }
});
export const createProvider = () => {
  const result = createGraphQLApiProvider({
    cache: setupCache()
  });
  if (typeof window !== 'undefined') {
    // @ts-expect-error expose apollo client on window
    window.apolloClient = result.client;
  }
  return result;
};
type ConfiguredProvider = ReturnType<typeof createProvider>;
type ConfiguredGraphQLApiProvider = ConfiguredProvider['GraphQLApiProvider'];
type ConfiguredApolloClient = ConfiguredProvider['client'];
let cachedProvider: ConfiguredGraphQLApiProvider | undefined;
let cachedApolloClient: ConfiguredApolloClient | undefined;
export const getGraphQLApiProvider = () => {
  if (typeof window === 'undefined') {
    return createProvider().GraphQLApiProvider;
  }
  if (!cachedProvider) {
    const result = createProvider();
    cachedProvider = result.GraphQLApiProvider;
    cachedApolloClient = result.client;
  }
  return cachedProvider;
};
export const GraphQLApiProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const Provider = getGraphQLApiProvider();
  return React.createElement(Provider, null, children);
};
const getApolloClient = () => {
  if (typeof window === 'undefined') {
    return createProvider().client;
  }
  if (!cachedApolloClient) {
    const result = createProvider();
    cachedProvider = result.GraphQLApiProvider;
    cachedApolloClient = result.client;
  }
  return cachedApolloClient;
};
export const apolloClient = new Proxy({}, {
  get(_target, property) {
    const client = getApolloClient();
    const value = client[property as keyof ConfiguredApolloClient];
    return typeof value === 'function' ? value.bind(client) : value;
  }
}) as ApolloClient<NormalizedCacheObject>;